import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Textarea} from '@/components/ui/textarea'
import {Campo} from '@/components/campo'
import {NativeSelect} from '@/components/native-select'
import {
  useEditCausa,
  useRemoveCausa,
  type Causa,
  type CausaCategoria,
  type CausaPatch,
  type CausaTipo,
} from '@/hooks/sgsst/incidentes/use-investigacion'
import {translateApiError} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

const CAUSA_TIPOS: CausaTipo[] = ['acto_subestandar', 'condicion_subestandar', 'factor_personal', 'factor_de_trabajo']

/** Catálogo cerrado (enum `CausaCategoria` del backend): va en select nativo, no en combobox. */
const CATEGORIAS_6M: CausaCategoria[] = ['persona', 'metodo', 'maquina', 'material', 'entorno', 'medicion']

export function TipoSelect({
  tipo,
  onChange,
  testId,
}: {
  tipo: CausaTipo
  onChange: (t: CausaTipo) => void
  testId: string
}) {
  const {t} = useTranslation()
  return (
    <NativeSelect data-testid={testId} value={tipo} onChange={(e) => onChange(e.target.value as CausaTipo)}>
      {CAUSA_TIPOS.map((tp) => (
        <option key={tp} value={tp}>
          {t(`investigacion.causaTipo.${tp}`)}
        </option>
      ))}
    </NativeSelect>
  )
}

export function CausaFila({
  causa,
  businessId,
  incidenteId,
  readOnly,
  numero,
  conCategoria = false,
}: {
  causa: Causa
  businessId: string
  incidenteId: string
  readOnly: boolean
  /** El puesto en la cadena de 5 porqués; las otras dos vistas no numeran. */
  numero?: number
  /** Solo en Ishikawa; Kairos no adivina la espina 6M a partir del texto. */
  conCategoria?: boolean
}) {
  const {t} = useTranslation()
  const [editando, setEditando] = useState(false)
  const [descripcion, setDescripcion] = useState(causa.descripcion)
  const [tipo, setTipo] = useState<CausaTipo>(causa.tipo)
  const [error, setError] = useState<string | null>(null)
  const editar = useEditCausa(businessId, incidenteId)
  const eliminar = useRemoveCausa(businessId, incidenteId)

  function aplicar(patch: CausaPatch, alGuardar?: () => void) {
    setError(null)
    editar.mutate(
      {causaId: causa.id, patch},
      {onSuccess: () => alGuardar?.(), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <li
      className="space-y-2 rounded-lg border border-l-[3px] border-border border-l-primary p-3"
      data-testid="causa-row">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          {numero !== undefined && <p className="font-mono text-xs text-muted-foreground tabular-nums">{numero}</p>}
          <p className="text-sm">{causa.descripcion}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {causa.tipo_label}
            {causa.catalogo_label && <span title={causa.catalogo_code ?? undefined}> · {causa.catalogo_label}</span>}
          </p>
        </div>
        {!readOnly && (
          <div className="flex shrink-0 items-center gap-1.5">
            <Button
              size="xs"
              variant="ghost"
              data-testid="causa-editar"
              onClick={() => {
                setDescripcion(causa.descripcion)
                setTipo(causa.tipo)
                setError(null)
                setEditando((v) => !v)
              }}>
              {t('investigacion.causas.editarCausa')}
            </Button>
            <Button
              size="xs"
              variant="ghost"
              data-testid="causa-eliminar"
              disabled={eliminar.isPending}
              onClick={() => {
                setError(null)
                eliminar.mutate(causa.id, {onError: (err) => setError(translateApiError(err))})
              }}>
              {t('investigacion.causas.eliminarCausa')}
            </Button>
          </div>
        )}
      </div>
      {conCategoria && !readOnly && (
        <Campo label={t('investigacion.causas.recategorizar')} htmlFor={`causa-categoria-${causa.id}`}>
          <NativeSelect
            id={`causa-categoria-${causa.id}`}
            data-testid="causa-categoria"
            value={causa.categoria ?? ''}
            disabled={editar.isPending}
            onChange={(e) => aplicar({categoria: e.target.value as CausaCategoria})}>
            {causa.categoria === null && <option value="">{t('investigacion.categoria.sin')}</option>}
            {CATEGORIAS_6M.map((code) => (
              <option key={code} value={code}>
                {t(`investigacion.categoria.${code}`)}
              </option>
            ))}
          </NativeSelect>
        </Campo>
      )}
      {editando && (
        <div className="space-y-2 border-t border-border pt-2">
          <Campo label={t('investigacion.causas.tipoLabel')}>
            <TipoSelect tipo={tipo} onChange={setTipo} testId="causa-editar-tipo" />
          </Campo>
          <Campo label={t('investigacion.causas.cualLabel')} htmlFor={`causa-editar-descripcion-${causa.id}`}>
            <Textarea
              maxLength={tope('TEXTO')}
              id={`causa-editar-descripcion-${causa.id}`}
              data-testid="causa-editar-descripcion"
              value={descripcion}
              minRows={2}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </Campo>
          <div className="flex flex-wrap gap-1.5">
            <Button
              size="sm"
              className="min-h-11 sm:min-h-0"
              data-testid="causa-guardar"
              disabled={descripcion.trim() === '' || editar.isPending}
              onClick={() => aplicar({descripcion: descripcion.trim(), tipo}, () => setEditando(false))}>
              {t('investigacion.causas.guardarCausa')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="min-h-11 sm:min-h-0"
              data-testid="causa-cancelar"
              onClick={() => setEditando(false)}>
              {t('investigacion.causas.cancelarEdicion')}
            </Button>
          </div>
        </div>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </li>
  )
}
